import { LegalHeading, List, Section, SubSection } from "./layout";

export default function Terms() {
  return (
    <>
      <title>Terms of Service — TitanSkip</title>
      <meta
        name="description"
        content="The terms governing your use of the TitanSkip skip tracing platform, API, and credits."
      />
      <link rel="canonical" href="https://titanskip.com/legal/terms" />
      <meta property="og:title" content="Terms of Service — TitanSkip" />
      <meta property="og:url" content="https://titanskip.com/legal/terms" />

      <LegalHeading title="Terms of Service" updated="May 14, 2026" />

      <Section title="1. Acceptance of Terms">
        <p>
          These Terms of Service (the &ldquo;Terms&rdquo;) govern your access to
          and use of the TitanSkip website, application, and APIs (collectively,
          the &ldquo;Service&rdquo;), operated by TitanSkip (&ldquo;we&rdquo;,
          &ldquo;us&rdquo;, or &ldquo;our&rdquo;). By creating an account,
          accessing the Service, or purchasing credits, you agree to be bound by
          these Terms. If you do not agree, you may not use the Service.
        </p>
      </Section>

      <Section title="2. Eligibility & Account Registration">
        <p>
          You must be at least 18 years old and have the authority to enter into
          this agreement on behalf of yourself or the entity you represent. You
          agree to provide accurate, current, and complete information when you
          register, and to keep that information up to date.
        </p>
        <p>
          You are responsible for safeguarding your account credentials and for
          all activity that occurs under your account. Notify us immediately of
          any unauthorized access.
        </p>
      </Section>

      <Section title="3. The Service">
        <p>
          TitanSkip provides skip tracing, LLC owner resolution, and DNC and
          litigation list scrubbing for property and contact data. Output is
          delivered via web download, API, and integrations with third-party
          customer relationship management and dialer systems.
        </p>
      </Section>

      <Section title="4. Acceptable Use">
        <p>You agree not to use the Service to:</p>
        <List>
          <li>Violate any applicable law or regulation, including the
            Telephone Consumer Protection Act (TCPA), CAN-SPAM, the Fair Credit
            Reporting Act (FCRA), or state and federal Do-Not-Call rules.</li>
          <li>Make eligibility determinations regarding credit, insurance,
            employment, housing, or other purposes governed by the FCRA.</li>
          <li>Engage in harassment, stalking, fraud, or any unlawful contact
            with the individuals whose data is returned.</li>
          <li>Resell, sublicense, or republish raw output from the Service to
            third parties as a substitute for them purchasing the Service.</li>
          <li>Reverse engineer, scrape, or attempt to derive the source code or
            underlying data structures of the Service.</li>
          <li>Interfere with the security or integrity of the Service or its
            infrastructure.</li>
        </List>
      </Section>

      <Section title="5. Credits, Billing, and Refunds">
        <SubSection title="Credit-based pricing">
          <p>
            Use of the Service is metered through credits purchased on a
            pay-as-you-go basis. Volume discounts apply automatically at the
            tiers published on the pricing page and may be adjusted from time
            to time.
          </p>
        </SubSection>
        <SubSection title="Payment">
          <p>
            All purchases are processed in U.S. dollars and are due immediately
            upon purchase. You authorize us, or our payment processor, to charge
            the payment method on file for any credits purchased.
          </p>
        </SubSection>
        <SubSection title="Refunds">
          <p>
            Credits are non-transferable and non-refundable except where
            required by law or where credits have been charged in error. Unused
            credits do not expire as long as your account remains in good
            standing.
          </p>
        </SubSection>
      </Section>

      <Section title="6. Data Accuracy & No Warranties">
        <p>
          TitanSkip aggregates data from public, commercial, and proprietary
          sources. We use reasonable efforts to deliver high-quality results,
          but we do not warrant that any individual record will be accurate,
          complete, or current. The Service is provided &ldquo;as is&rdquo; and
          &ldquo;as available&rdquo; without warranties of any kind, express or
          implied, including merchantability, fitness for a particular purpose,
          and non-infringement.
        </p>
      </Section>

      <Section title="7. Compliance Responsibility">
        <p>
          You are solely responsible for ensuring that your use of the Service,
          including any subsequent contact with individuals whose data is
          returned, complies with all applicable laws. Our DNC and litigation
          scrubbing is provided as a tool to assist in compliance and does not
          substitute for your own legal review or counsel.
        </p>
      </Section>

      <Section title="8. Intellectual Property">
        <p>
          The Service, including all software, design, content, and trademarks,
          is owned by TitanSkip or its licensors and is protected by U.S. and
          international intellectual property laws. We grant you a limited,
          non-exclusive, non-transferable, revocable license to use the Service
          solely as permitted by these Terms.
        </p>
        <p>
          You retain ownership of any input data you submit to the Service.
          You grant us a worldwide, royalty-free license to host, process, and
          transmit that data solely as necessary to provide the Service.
        </p>
      </Section>

      <Section title="9. Termination">
        <p>
          We may suspend or terminate your access to the Service at any time,
          with or without notice, for any conduct that we reasonably believe
          violates these Terms or is otherwise harmful to TitanSkip, its users,
          or third parties. Upon termination, your right to access the Service
          will cease immediately, but Sections 4, 6, 7, 10, 11, 12, and 13 will
          survive.
        </p>
      </Section>

      <Section title="10. Limitation of Liability">
        <p>
          To the maximum extent permitted by law, TitanSkip and its officers,
          employees, and affiliates will not be liable for any indirect,
          incidental, special, consequential, or punitive damages, or any loss
          of profits or revenue, whether incurred directly or indirectly, or
          any loss of data, use, goodwill, or other intangible losses arising
          from your use of the Service.
        </p>
        <p>
          Our total liability for any claim arising out of or relating to the
          Service is limited to the amount you paid us in the twelve months
          preceding the event giving rise to the claim, or one hundred U.S.
          dollars, whichever is greater.
        </p>
      </Section>

      <Section title="11. Indemnification">
        <p>
          You agree to defend, indemnify, and hold harmless TitanSkip from any
          claim, demand, loss, or expense (including reasonable attorneys&rsquo;
          fees) arising out of your use of the Service, your violation of these
          Terms, or your violation of any third-party right or applicable law.
        </p>
      </Section>

      <Section title="12. Governing Law & Dispute Resolution">
        <p>
          These Terms are governed by the laws of the State of Delaware,
          without regard to conflict-of-law principles. Any dispute arising out
          of or relating to these Terms or the Service will be resolved by
          binding arbitration administered by the American Arbitration
          Association under its Commercial Arbitration Rules, except that
          either party may seek injunctive relief in a court of competent
          jurisdiction to protect its intellectual property.
        </p>
      </Section>

      <Section title="13. Changes to These Terms">
        <p>
          We may update these Terms from time to time. If we make material
          changes, we will notify you by email or by posting a notice on the
          Service prior to the effective date. Continued use of the Service
          after the effective date constitutes acceptance of the revised Terms.
        </p>
      </Section>

      <Section title="14. Contact">
        <p>
          Questions about these Terms can be sent to{" "}
          <a
            href="mailto:legal@titanskip.com"
            className="text-main-300 underline-offset-4 hover:underline"
          >
            legal@titanskip.com
          </a>
          .
        </p>
      </Section>
    </>
  );
}
